import { useId } from 'react';

import type { SupportItemInput } from '../../schemas/project.schema';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import { cn } from '@/shared/lib/utils';
import type { SupportGroup, SupportTypeOption } from '@/shared/types/domain';

interface SupportChecklistProps {
  supportTypes: SupportTypeOption[];
  items: SupportItemInput[];
  /** Marcar/desmarcar é discreto: salva na hora. */
  onToggle: (typeId: string, checked: boolean) => void;
  /** Texto do pedido: debounce. */
  onChangeRequest: (typeId: string, request: string) => void;
  /** Data: discreto. */
  onChangeNeededBy: (typeId: string, neededBy: string) => void;
}

const GROUP_ORDER: SupportGroup[] = ['media', 'technical', 'arts', 'operations'];

/**
 * Os 14 tipos de apoio, em quatro grupos (decisão 22).
 *
 * **Marcar um tipo revela o campo de detalhe, e a sugestão do catálogo
 * (`request_hint`) vira o PLACEHOLDER dele** — nunca valor inicial.
 * Pré-preencher faria o voluntário enviar o exemplo como se fosse o pedido
 * dele, e o ministério de Som receberia catorze projetos pedindo "mesa de 32
 * canais".
 *
 * O texto continua opcional: marcar já é informação ("vou precisar de som").
 * O que a UI impede é o contrário — texto sem tipo marcado, que não teria onde
 * ser gravado.
 */
export function SupportChecklist({
  supportTypes,
  items,
  onToggle,
  onChangeRequest,
  onChangeNeededBy,
}: SupportChecklistProps) {
  const fieldPrefix = useId();

  const groups = GROUP_ORDER.map((group) => ({
    group,
    label: messages.supportGroups[group],
    types: supportTypes.filter((type) => type.group_name === group),
  })).filter((entry) => entry.types.length > 0);

  return (
    <div className="space-y-6">
      {groups.map(({ group, label, types }) => (
        <fieldset key={group}>
          <legend className="mb-2 text-sm font-medium text-muted-foreground">{label}</legend>

          <div className="space-y-2">
            {types.map((type) => {
              const item = items.find((entry) => entry.support_type_id === type.id);
              const checked = Boolean(item);
              const requestId = `${fieldPrefix}-${type.id}-pedido`;
              const neededById = `${fieldPrefix}-${type.id}-prazo`;

              return (
                <div
                  key={type.id}
                  className={cn(
                    'rounded-md border bg-card px-3 py-2.5 transition-colors duration-base ease-out',
                    checked ? 'border-primary/30' : 'border-border',
                  )}
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={checked}
                      onChange={(event) => onToggle(type.id, event.target.checked)}
                    />
                    {type.name}
                  </label>

                  {/* O bloco continua MONTANDO e DESMONTANDO com a marcação —
                      mantê-lo sempre no DOM, escondido, deixaria dois campos
                      por tipo de apoio no caminho do Tab e no leitor de tela
                      (são 14 tipos: 28 campos fantasma). O preço é não haver
                      animação de saída; a de entrada é o que a pessoa vê. */}
                  {checked ? (
                    <div className="mt-3 animate-reveal space-y-3 pl-6">
                      <div className="space-y-1.5">
                        <label htmlFor={requestId} className="text-sm font-medium leading-none">
                          {messages.form.supportRequestLabel(type.name)}
                        </label>
                        <Textarea
                          id={requestId}
                          rows={2}
                          maxLength={500}
                          value={item?.request ?? ''}
                          placeholder={type.request_hint ?? undefined}
                          onChange={(event) => onChangeRequest(type.id, event.target.value)}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor={neededById} className="text-sm font-medium leading-none">
                          {messages.form.supportNeededBy}
                        </label>
                        <Input
                          id={neededById}
                          type="date"
                          className="max-w-44"
                          value={item?.needed_by ?? ''}
                          onChange={(event) => onChangeNeededBy(type.id, event.target.value)}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
